import type { SearchAdapterOptions } from "src/types/adapters/search.type";
import type { PropsWithConstructorClient } from ".";
import type {
  BrowseableEntity,
  BrowseParams,
  BrowseResult,
} from "src/types/adapters/browse.type";
import type {
  BrowseResultData,
  Facet,
  GetBrowseResultsResponse,
} from "@constructor-io/constructorio-client-javascript";
import {
  BROWSE_ENTITY_TO_CONSTRUCTOR_SECTION,
  DEFAULT_FACET,
  DEFAULT_FILTERS,
} from "./utils/constants";
import {
  getFacetName,
  getTotalResults,
  mapToAllBrands,
  mapToBrands,
  mapToCatalogProduct,
  mapToCategory,
  toConstructorFacets,
  toListingFacets,
} from "./utils/helpers";
import type { ConstructorProductData } from "./utils/types";

const MAX_RESULTS_PER_PAGE = 200;

const FACET_RESULTS_ENTITY: readonly BrowseableEntity[] = [
  "all-brands",
  "facets",
] as const;

const DEFAULT_FACETS_ENTITY: readonly BrowseableEntity[] = [
  "all-brands",
  "facets",
  "products",
] as const;

const MAP_ENTITY: {
  [E in BrowseableEntity]: (
    rawResults: BrowseResultData[] | Facet[]
  ) => BrowseResult<E>["results"];
} = {
  products: (rawResults) =>
    (rawResults as BrowseResultData[]).map((item) =>
      mapToCatalogProduct(item.data as ConstructorProductData, item.value)
    ),
  brands: (rawResults) => mapToBrands(rawResults as BrowseResultData[]),
  "all-brands": (rawResults) => mapToAllBrands(rawResults as Facet[]),
  categories: (rawResults) =>
    rawResults.flatMap(
      (result) => mapToCategory(result as BrowseResultData) ?? []
    ),
  facets: (rawResults) => toListingFacets(rawResults as Facet[]),
};

export async function browse<E extends BrowseableEntity>({
  client,
  params,
}: PropsWithConstructorClient<{
  options: SearchAdapterOptions;
  params: BrowseParams<E>;
}>): Promise<BrowseResult<E>> {
  try {
    const section = BROWSE_ENTITY_TO_CONSTRUCTOR_SECTION[params.entity];
    const facetName = getFacetName(params) || DEFAULT_FACET.facetName;
    const facetValue = params.facetValue || DEFAULT_FACET.facetValue;
    const resultsPerPage =
      params.parameters?.hitsPerPage ?? MAX_RESULTS_PER_PAGE;
    const filters = {
      ...(DEFAULT_FACETS_ENTITY.includes(params.entity) ? DEFAULT_FILTERS : {}),
      ...(params.parameters?.filters
        ? toConstructorFacets(params.parameters?.filters)
        : {}),
    };

    const first = await client.browse(facetName, facetValue, {
      section,
      filters,
      resultsPerPage,
      page: 1,
    });

    const totalResults = getTotalResults(first.response);

    const totalPages = !FACET_RESULTS_ENTITY.includes(params.entity)
      ? Math.ceil((totalResults || 1) / resultsPerPage)
      : 1;

    const extraPages: Promise<GetBrowseResultsResponse>[] = [];
    let rest: GetBrowseResultsResponse[] = [];

    if (totalPages > 1) {
      for (let page = 2; page <= totalPages; page++) {
        extraPages.push(
          client.browse(facetName, facetValue, {
            section,
            resultsPerPage,
            page,
          })
        );
      }

      rest = await Promise.all(extraPages);
    }

    const rawResults = !FACET_RESULTS_ENTITY.includes(params.entity)
      ? [
          ...first.response.results,
          ...rest.flatMap((data) => data.response.results),
        ]
      : first.response.facets;

    const results = MAP_ENTITY[params.entity](
      rawResults
    ) as BrowseResult<E>["results"];

    return {
      results,
      totalResults,
    };
  } catch (error) {
    console.error(
      "[Constructor Adapter] An error occurred while fetching browse results:",
      error
    );

    const emptyResults = (
      params.entity === "facets" ? new Map() : []
    ) as BrowseResult<E>["results"];

    return { results: emptyResults, totalResults: 0 };
  }
}
